import dayjs from 'dayjs';
import { cloneDeep, isEqual, omit, range } from 'lodash';
import type { LiteralUnion } from 'type-fest';
import * as XLSX from 'xlsx';
import type { WorkSheet } from 'xlsx';
import { getFramework } from '@/framework';
import {
    ILayerProps,
    MIN_FONT_SIZE,
    TextAlignType,
    type BaseCells,
    type Border,
    type BorderStyle,
    type Cell,
    type CellStyle,
    type ITableLayer,
    type ITableLayerProps,
    type PureCell,
} from '@/version-types';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    MAX_COL_COUNT,
    MAX_ROW_COUNT,
    MIN_BORDER_WIDTH,
    SIDE_BAR_BOLD,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../default-configs';
import { prepareTableLayerDocumentFonts } from '../font-measurement';
import { getTableByCommonPreset } from '../presets/common';
import { isEqualBorder } from './cell';
import fixTableLayout from './fix-table-layout';

const tableConstants = {
    borderWidth: 1,
    borderColor: '#b4b8bfff',
    cellWidth: 60,
    cellHeight: 12,
} as const;

/** ['A1', 'A2', 'B1', 'B2', ...] */
const keyRegExp = /([A-Z]+)([0-9]+)/i;

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// eslint-disable-next-line tsdoc/syntax
const getIndexByCap = (/** 'A' -> 0 or 'B' -> 1 or 'Z' -> 25 or 'AA' -> 26 */ capText: string): number => {
    if (!/^[A-Z]+$/i.test(capText)) return -1;
    // 实际上就是26进制, 最后再减一
    // 26进制: 'A' -> 1 or 'B' -> 2 or 'Z' -> 26 or 'AA' -> 27
    let result = 0;
    capText
        .split('')
        .reverse()
        .forEach((str, index) => {
            const digitNum = index + 1;
            const num = alphabet.indexOf(str) + 1;
            result += num * Math.pow(26, digitNum - 1);
        });
    return result - 1;
};

const parsePositionKey = (/** 如 'A1' */ positionKey: string) => {
    const regMatchResult = positionKey.match(keyRegExp)!;
    const capText = regMatchResult[1];
    const rowIndex = +regMatchResult[2] - 1;
    const colIndex = getIndexByCap(capText);

    return {
        capText,
        rowIndex,
        colIndex,
    };
};

/** 0为'A', 1为'B', ..., 25为'Z', 26为'AA', ...*/
const getCapText = (index: number) => {
    const maxIndex = (26 + 1) * 26 - 1;
    if (index > maxIndex) {
        // 先只实现两位的, 三位的用不到
        throw '';
    }

    if (index < 26) return alphabet[index];
    const n1 = alphabet[Math.floor(index / 26) - 1];
    const n2 = alphabet[index % 26];
    return `${n1}${n2}`;
};

const getPositionKey = ({ xlsxColIndex, xlsxRowIndex }: { xlsxColIndex: number; xlsxRowIndex: number }): string =>
    `${getCapText(xlsxColIndex)}${xlsxRowIndex + 1}`;

type SheetOfXlsxPopulate = Record<any, any>;

/** 解析xlsx所需的数据 */
type XLSXInfo = {
    /** xlsx库解析出来的worksheet数据, 用来获取单元格位置和信息 */
    worksheet: WorkSheet;
    /** 用xlsx-populate库解析出来的sheet数据, 用于获取单元格样式和边框信息 */
    sheetOfXlsxPopulate?: SheetOfXlsxPopulate;
};

type Unknown = Record<string, any>;
type Color_XlsxPopulate =
    | {
          /**
           * 两种值:
           * 1. 不带#符号的16进制, 结构为argb (https://github.com/dtjohnson/xlsx-populate/issues/52)
           * 2. 不带#符号的16进制, 结构为rgb
           */
          rgb: string;
      }
    | {
          /** 主题色索引, 由于excel多个版本的主题色列表不是一致的, 没法处理, 所以这里不处理 (非要处理的话, 可以看看xlsx库的付费版本说不定有) */
          theme: number;
          tint: number;
      }
    | Unknown;

const getRgbaFromColor_XlsxPopulate = (color: Color_XlsxPopulate | undefined): `#${string}` | undefined => {
    const getRGBA_From_ARGB_Or_RGB = (text: string) => (text.length === 8 ? text.slice(2) + text.slice(0, 2) : text);
    return (color as any)?.rgb ? `#${getRGBA_From_ARGB_Or_RGB((color as any).rgb)}` : undefined;
};

type BorderStyle_XlsxPopulate =
    | 'thin'
    | 'hair'
    | 'dotted'
    | 'dashed'
    | 'dashDot'
    | 'dashDotDot'
    | 'double'
    | 'medium'
    | 'mediumDashed'
    | 'mediumDashDot'
    | 'mediumDashDotDot'
    | 'slantDashDot'
    | 'thick';

type XlsxBorderStyleInfo =
    | {
          /** 默认为黑色边框 */
          color?: Color_XlsxPopulate;
          style: BorderStyle_XlsxPopulate;
      }
    | Unknown;

/** excel兜底的网格颜色 */
const getEmptyBorder = (): Border => ({
    color: tableConstants.borderColor,
    style: 'line-0',
    width: tableConstants.borderWidth,
});

const getRowOrColIndexBorder = ({
    sheetOfXlsxPopulate,
    index: indexArg,
    mode,
    xlsxRange: { startColIndex, startRowIndex, endColIndex, endRowIndex },
}: {
    sheetOfXlsxPopulate: SheetOfXlsxPopulate;
    index: number;
    mode: 'rowIndex' | 'colIndex';
    xlsxRange: {
        startColIndex: number;
        startRowIndex: number;
        endColIndex: number;
        endRowIndex: number;
    };
}): Border => {
    /** 值为null意味着: 找不到对应的borderStyle */
    const borderStyleMap: Record<BorderStyle_XlsxPopulate, BorderStyle | null> = {
        thin: 'line-0',
        hair: 'line-2',
        dotted: 'line-2',
        dashed: 'line-1',
        dashDot: 'line-3',
        dashDotDot: 'line-4',
        double: null,
        medium: 'line-0',
        mediumDashed: 'line-1',
        mediumDashDot: 'line-3',
        mediumDashDotDot: 'line-4',
        slantDashDot: null,
        thick: 'line-0',
    };
    // 边框粗细的倍数
    const borderWidthTimeMap: Record<BorderStyle_XlsxPopulate, number> = {
        thin: 1,
        hair: 1,
        dotted: 1,
        dashed: 1,
        dashDot: 1,
        dashDotDot: 1,
        double: 2,
        medium: 2,
        mediumDashed: 2,
        mediumDashDot: 2,
        mediumDashDotDot: 2,
        slantDashDot: 2,
        thick: 3,
    };

    let borderDir: 'top' | 'right' | 'bottom' | 'left';
    let index: number;
    if (mode === 'rowIndex') {
        const isLastIndex = indexArg === endRowIndex - startRowIndex;
        borderDir = isLastIndex ? 'bottom' : 'top';
        index = isLastIndex ? indexArg - 1 : indexArg;
    } else {
        const isLastIndex = indexArg === endColIndex - startColIndex;
        borderDir = isLastIndex ? 'right' : 'left';
        index = isLastIndex ? indexArg - 1 : indexArg;
    }
    const tryGetBorder = (
        cellPosition: { rowIndex: number; colIndex: number },
        borderDir: 'top' | 'right' | 'bottom' | 'left',
    ): Border | null => {
        const positionKey = getPositionKey({
            xlsxColIndex: startColIndex + cellPosition.colIndex,
            xlsxRowIndex: startRowIndex + cellPosition.rowIndex,
        });
        const borderInfo = sheetOfXlsxPopulate.cell(positionKey).style(`${borderDir}Border`) as
            | XlsxBorderStyleInfo
            | undefined;
        if (!borderInfo) return null;
        return {
            // 若excel有边框信息, 但拿不到边框颜色数据, 则边框为黑色
            color: getRgbaFromColor_XlsxPopulate(borderInfo.color) ?? '#000000ff',
            style: borderStyleMap[borderInfo.style] ?? 'line-0',
            width: tableConstants.borderWidth * (borderInfo.style ? borderWidthTimeMap[borderInfo.style] : 1),
        };
    };
    let resultBorder: Border | null = null;
    for (let i = 0; i < (mode === 'rowIndex' ? endColIndex : endRowIndex); i++) {
        const border = tryGetBorder(
            {
                rowIndex: mode === 'rowIndex' ? index : i,
                colIndex: mode === 'rowIndex' ? i : index,
            },
            borderDir,
        );
        if (border) resultBorder = border;
    }
    return resultBorder ?? getEmptyBorder();
};

const getCellStyleByXlsxPopulate = ({
    sheetOfXlsxPopulate,
    positionKey,
}: {
    sheetOfXlsxPopulate: SheetOfXlsxPopulate;
    /** 如 'A1' */
    positionKey: string;
}): Omit<CellStyle, 'border'> => {
    type CellStyle_XlsxPopulate = {
        bold: boolean;
        italic: boolean;
        /** 有双下划线的情况, 也当成单下划线处理, 因为编辑器只有单下划线功能 */
        underline: boolean | LiteralUnion<'double', string>;
        /** 删除线 */
        strikethrough?: boolean;
        /** 默认12 */
        fontSize?: number;
        /** 不管, 反正也用不了 */
        fontFamily: string;
        fontColor?: Color_XlsxPopulate;
        /** 默认左对齐 */
        horizontalAlignment?: LiteralUnion<'top' | 'center' | 'bottom' | 'justify', string>;
        /** 有些版本默认是低端对齐, 有些版本默认是居中对齐, 反正如果值为undefined, 就直接当底部对齐处理 */
        verticalAlignment?: LiteralUnion<'top' | 'center' | 'bottom', string>;
        wrapText?: boolean;
        fill?:
            | {
                  type: LiteralUnion<'solid', string>;
                  color: Color_XlsxPopulate;
              }
            | string
            | Unknown;
    };

    const style = sheetOfXlsxPopulate
        .cell(positionKey)
        .style([
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'fontSize',
            'fontFamily',
            'fontColor',
            'horizontalAlignment',
            'verticalAlignment',
            'wrapText',
            'fill',
        ]) as CellStyle_XlsxPopulate;
    return {
        backgroundColor:
            getRgbaFromColor_XlsxPopulate((style.fill as any)?.color) ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
        verticalAlign:
            style.verticalAlignment && ['top', 'center', 'bottom'].includes(style.verticalAlignment)
                ? (style.verticalAlignment as 'top' | 'center' | 'bottom')
                : 'bottom',

        disableAutoWrap: !style.wrapText,
        fontSize: style.fontSize ?? 12,
        textAlign:
            style.horizontalAlignment && ['top', 'center', 'bottom', 'justify'].includes(style.horizontalAlignment)
                ? (style.horizontalAlignment as TextAlignType)
                : TextAlignType.LEFT,
        color: style.fontColor ? getRgbaFromColor_XlsxPopulate(style.fontColor) : DEFAULT_TABLE_CELL__TEXT_COLOR,
        bold: style.bold,
        italic: style.italic,
        lineThrough: style.strikethrough,
        underline: !!style.underline,
    };
};

type FormulaCellProp = ITableLayerProps<true>['cells'];

/** 超出行列限制则自动舍弃 */
const getTableInfoByXLSXSheet = ({
    worksheet,
    sheetOfXlsxPopulate,
}: XLSXInfo): {
    cellsProp: FormulaCellProp;
    baseCells: BaseCells;
    styles: ITableLayerProps['styles'];
    isExceed: boolean;
} => {
    /**
     * 修复异常数据 (从腾讯文档导出的xlsx的!ref中的范围有问题)
     * issue: https://github.com/SheetJS/sheetjs/issues/1601#issuecomment-1411759149
     */
    const update_sheet_range = (ws: WorkSheet) => {
        const range = {
            s: {
                r: Infinity,
                c: Infinity,
            },
            e: {
                r: 0,
                c: 0,
            },
        };
        Object.keys(ws)
            .filter((x) => !x.startsWith('!'))
            .map(XLSX.utils.decode_cell)
            .forEach((x) => {
                range.s.c = Math.min(range.s.c, x.c);
                range.s.r = Math.min(range.s.r, x.r);
                range.e.c = Math.max(range.e.c, x.c);
                range.e.r = Math.max(range.e.r, x.r);
            });
        ws['!ref'] = XLSX.utils.encode_range(range);
    };
    update_sheet_range(worksheet);

    console.log('===getTableInfoByXLSXSheet', {
        worksheet,
        sheetOfXlsxPopulate,
    });
    const getColumnSizeByXlsxIndex = (colIndex: number) => {
        if (!sheetOfXlsxPopulate) return tableConstants.cellWidth;
        const positionKey = getCapText(colIndex);
        const width = sheetOfXlsxPopulate.column(positionKey).width() as number | undefined;
        if (typeof width === 'undefined') return tableConstants.cellWidth;
        // excel不知道什么毛病, 宽度的值就是比高度小个7倍左右
        return width * 7 + TABLE_CELL_HORIZONTAL_PADDING * 2;
    };
    const getRowSizeByXlsxIndex = (rowIndex: number) => {
        if (!sheetOfXlsxPopulate) return tableConstants.cellHeight;
        const height = sheetOfXlsxPopulate.row(rowIndex + 1).height();
        if (typeof height === 'undefined') return tableConstants.cellHeight;
        return height;
    };

    type CellInfo = {
        text: string;
        position: {
            startColIndex: number;
            startRowIndex: number;
            endColIndex: number;
            endRowIndex: number;
        };
        style?: CellStyle;
    };
    /** xlsx中的坐标 */
    const mergeCellXlsxPositions = worksheet['!merges']?.map<CellInfo['position']>((sheetMerge) => {
        const { c: startColIndex, r: startRowIndex } = sheetMerge.s;
        const { c: endColIndex, r: endRowIndex } = sheetMerge.e;
        return {
            startColIndex,
            startRowIndex,
            endColIndex: endColIndex + 1,
            endRowIndex: endRowIndex + 1,
        };
    });

    const { tableRange, isExceed } = (() => {
        let isExceed = false;
        // 表格范围, 比如: 'A1:C3', 左上角网格的位置为A1, 右下角网格的位置为C3
        const defaultRange = `A1:${getCapText(MAX_COL_COUNT - 1)}${MAX_ROW_COUNT}`;
        const range = worksheet['!ref'] ?? defaultRange;
        const [startPositionKey, endPositionKey = 'A1'] = range.split(':');
        const startPosition = parsePositionKey(startPositionKey);
        const endPosition = parsePositionKey(endPositionKey);
        const { colIndex: startColIndex, rowIndex: startRowIndex } = startPosition;
        let endColIndex = endPosition.colIndex + 1;
        let endRowIndex = endPosition.rowIndex + 1;

        if (startColIndex >= endColIndex || startRowIndex >= endRowIndex) {
            throw new Error('Invalid table range');
        }

        // 范围限制
        if (endColIndex - startColIndex > MAX_COL_COUNT) {
            endColIndex = startColIndex + MAX_COL_COUNT;
            isExceed = true;
        }
        if (endRowIndex - startRowIndex > MAX_ROW_COUNT) {
            endRowIndex = startRowIndex + MAX_ROW_COUNT;
            isExceed = true;
        }
        return {
            tableRange: {
                startColIndex,
                startRowIndex,
                endColIndex,
                endRowIndex,
            },
            isExceed,
        };
    })();

    const baseCells: BaseCells = {
        horizontal: range(tableRange.startColIndex, tableRange.endColIndex).map(getColumnSizeByXlsxIndex),
        vertical: range(tableRange.startRowIndex, tableRange.endRowIndex).map(getRowSizeByXlsxIndex),
    };

    /** 水平方向的borders, 按rowIndex排序 */
    const hBorders = range(baseCells.vertical.length + 1).map((index) =>
        sheetOfXlsxPopulate
            ? getRowOrColIndexBorder({
                  sheetOfXlsxPopulate,
                  mode: 'rowIndex',
                  index,
                  xlsxRange: tableRange,
              })
            : getEmptyBorder(),
    );
    /** 竖直方向的borders, 按colIndex排序 */
    const vBorders = range(baseCells.horizontal.length + 1).map((index) =>
        sheetOfXlsxPopulate
            ? getRowOrColIndexBorder({
                  sheetOfXlsxPopulate,
                  mode: 'colIndex',
                  index,
                  xlsxRange: tableRange,
              })
            : getEmptyBorder(),
    );

    const getCellText = (xlsxCellInfo: any) => {
        if (!xlsxCellInfo) return '';
        const text =
            xlsxCellInfo.v instanceof Date
                ? dayjs(xlsxCellInfo.v).format('YYYY/MM/DD HH:mm:ss')
                : `${xlsxCellInfo.w ?? xlsxCellInfo.v ?? ''}`;
        return text;
    };

    const getCellInfo = (/** 如 'A1' */ positionKey: string): CellInfo => {
        const xlsxCellInfo = worksheet[positionKey];
        const text = getCellText(xlsxCellInfo);

        const { rowIndex: startRowIndex, colIndex: startColIndex } = parsePositionKey(positionKey);
        const targetMergedCellXlsxPosition = mergeCellXlsxPositions?.find(
            (position) => position.startColIndex === startColIndex && position.startRowIndex === startRowIndex,
        );
        const cellXlsxPosition = targetMergedCellXlsxPosition ?? {
            startColIndex,
            startRowIndex,
            endColIndex: startColIndex + 1,
            endRowIndex: startRowIndex + 1,
        };
        cellXlsxPosition.endColIndex = Math.min(cellXlsxPosition.endColIndex, tableRange.endColIndex);
        cellXlsxPosition.endRowIndex = Math.min(cellXlsxPosition.endRowIndex, tableRange.endRowIndex);
        // 将xlsx位置转换为最终位置
        const position = {
            startColIndex: cellXlsxPosition.startColIndex - tableRange.startColIndex,
            startRowIndex: cellXlsxPosition.startRowIndex - tableRange.startRowIndex,
            endColIndex: cellXlsxPosition.endColIndex - tableRange.startColIndex,
            endRowIndex: cellXlsxPosition.endRowIndex - tableRange.startRowIndex,
        };

        const styleWithoutBorder = sheetOfXlsxPopulate
            ? getCellStyleByXlsxPopulate({
                  sheetOfXlsxPopulate,
                  positionKey,
              })
            : {};
        const styleBorderProp: CellStyle['border'] = {
            top: cloneDeep(hBorders[cellXlsxPosition.startRowIndex] ?? getEmptyBorder()),
            right: cloneDeep(vBorders[cellXlsxPosition.endColIndex] ?? getEmptyBorder()),
            bottom: cloneDeep(hBorders[cellXlsxPosition.endRowIndex] ?? getEmptyBorder()),
            left: cloneDeep(vBorders[cellXlsxPosition.startColIndex] ?? getEmptyBorder()),
        };
        return {
            text,
            position,
            style: {
                ...styleWithoutBorder,
                border: styleBorderProp,
            },
        };
    };

    const cellPositionKeys = Object.keys(worksheet)
        .filter((k) => !k.startsWith('!'))
        .filter((k) => {
            const { colIndex, rowIndex } = parsePositionKey(k);
            return (
                tableRange.startColIndex <= colIndex &&
                colIndex < tableRange.endColIndex &&
                tableRange.startRowIndex <= rowIndex &&
                rowIndex < tableRange.endRowIndex
            );
        });
    // 补充positions, 因为xlsx的库的worksheet的key只会存放有单元格信息的数据, 像边框设置、合并单元格的操作, 会被忽略, 需要用 worksheet['!ref'] 获取范围
    {
        /** 如果当前位置位于合并单元格内, 且该位置不能表示该合并单元格的位置, 则忽略 */
        const shouldIgnore = ({ xlsxColIndex, xlsxRowIndex }: { xlsxColIndex: number; xlsxRowIndex: number }) =>
            !!mergeCellXlsxPositions?.some(({ startColIndex, startRowIndex, endColIndex, endRowIndex }) => {
                const isMergedCellPosition = xlsxColIndex === startColIndex && xlsxRowIndex === startRowIndex;
                if (isMergedCellPosition) return false;
                return (
                    startColIndex <= xlsxColIndex &&
                    xlsxColIndex < endColIndex &&
                    startRowIndex <= xlsxRowIndex &&
                    xlsxRowIndex < endRowIndex
                );
            });
        for (let xlsxColIndex = tableRange.startColIndex; xlsxColIndex < tableRange.endColIndex; xlsxColIndex++) {
            for (let xlsxRowIndex = tableRange.startRowIndex; xlsxRowIndex < tableRange.endRowIndex; xlsxRowIndex++) {
                const positionKey = getPositionKey({
                    xlsxColIndex,
                    xlsxRowIndex,
                });

                // 记录新单元格位置
                if (cellPositionKeys.includes(positionKey)) continue;
                if (shouldIgnore({ xlsxColIndex, xlsxRowIndex })) continue;
                cellPositionKeys.push(positionKey);
            }
        }
    }

    const cellInfoList = cellPositionKeys.map(getCellInfo);

    const stylePool: { style: CellStyle; styleId: string }[] = [];
    /** 返回styleId */
    function appendNewStyle(style: CellStyle): string {
        const isEqualBorderProp = (a: NonNullable<CellStyle['border']>, b: NonNullable<CellStyle['border']>) =>
            isEqualBorder(a.top, b.top) &&
            isEqualBorder(a.right, b.right) &&
            isEqualBorder(a.bottom, b.bottom) &&
            isEqualBorder(a.left, b.left);
        const poolItem = stylePool.find((item) => {
            if (Boolean(item.style.border) !== Boolean(style.border)) return false;
            if (item.style.border && style.border && !isEqualBorderProp(item.style.border, style.border)) return false;
            return isEqual(omit(item.style, 'border'), omit(style, 'border'));
        });
        if (poolItem) return poolItem.styleId;

        // id从2开始, 因为1是默认样式
        const newStyleId = (Math.max(1, ...stylePool.map((s) => +s.styleId)) + 1).toString();
        stylePool.push({
            style,
            styleId: newStyleId,
        });
        return newStyleId;
    }

    // 初始化cellsProp, 用null占位
    const cellsProp: FormulaCellProp = range(baseCells.vertical.length).map(() =>
        Array(baseCells.horizontal.length).fill(null),
    );
    // 补充cells
    cellInfoList.forEach(({ position: { startColIndex, startRowIndex, endColIndex, endRowIndex }, text, style }) => {
        const styleIds: string[] = [];
        if (style) {
            const newStyleId = appendNewStyle(style);
            styleIds.push(newStyleId);
        }
        const cell: PureCell = {
            vIndex: startRowIndex,
            hIndex: startColIndex,
            colSpan: endColIndex - startColIndex,
            rowSpan: endRowIndex - startRowIndex,
            content: text,
            styleIds,
        };
        cellsProp[cell.vIndex][cell.hIndex] = cell;
    });
    // 基于baseCells补充无数据的单元格
    for (let hIndex = 0; hIndex < baseCells.horizontal.length; hIndex++) {
        for (let vIndex = 0; vIndex < baseCells.vertical.length; vIndex++) {
            const isExisting = cellInfoList.some(
                ({ position: { startColIndex, startRowIndex, endColIndex, endRowIndex } }) =>
                    startColIndex <= hIndex && hIndex < endColIndex && startRowIndex <= vIndex && vIndex < endRowIndex,
            );
            if (isExisting) {
                continue;
            }
            const cell: PureCell = {
                vIndex,
                hIndex,
                colSpan: 1,
                rowSpan: 1,
                content: '',
                styleIds: [],
            };
            cellsProp[vIndex][hIndex] = cell;
        }
    }
    const getDefaultBorder = (): Border => ({
        color: tableConstants.borderColor,
        width: tableConstants.borderWidth,
        style: 'line-0',
    });
    return {
        cellsProp,
        baseCells,
        styles: {
            '1': {
                border: {
                    top: getDefaultBorder(),
                    right: getDefaultBorder(),
                    bottom: getDefaultBorder(),
                    left: getDefaultBorder(),
                },
            },
            ...Object.fromEntries(stylePool.map((item) => [item.styleId, item.style])),
        },
        isExceed,
    };
};

/** 获取用到的style. 没被引用到的style可以去掉了. 一方面是减轻formula数据量, 一方面是为了优化react render计算时长 */
export const getUsingStyles = (layer: ITableLayer | ITableLayer<true>) => {
    const { allStyleId, cells: cellsProp, styles } = layer.data;
    const usingStyles = cloneDeep(styles);
    const usedStyleIds = [allStyleId, ...(cellsProp.flat().filter(Boolean) as Cell[]).map((c) => c.styleIds).flat()];
    for (const styleId in styles) {
        if (usedStyleIds.includes(styleId)) continue;
        delete usingStyles[styleId];
    }
    return usingStyles;
};

/**
 * 根据画布做放大处理 (不需要缩小)
 * 表格的整体缩放, 适配画布大小: 扩大倍数 = 画布最长的边的长度 / 600 (没到极限的情况下)
 * 注: 缩放后需要再手动修复一下表格数据
 */
export const getScaledTableLayerByStageSize = <L extends ITableLayer | ITableLayer<true>>(
    tableLayer: L,
    scale: number,
): L => {
    const newTableLayer = cloneDeep(tableLayer);
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        styles,
    } = newTableLayer.data;
    const styleList = Object.values(styles) as CellStyle[];
    styleList.forEach((s) => {
        if (s.border) {
            const borderList = Object.values(s.border) as Border[];
            borderList.forEach((b) => (b.width = Math.max(MIN_BORDER_WIDTH, Math.round(b.width * scale))));
        }
        if (s.fontSize) {
            s.fontSize = Math.max(MIN_FONT_SIZE, Math.round(s.fontSize * scale));
        }
    });
    newTableLayer.width *= scale;
    newTableLayer.height *= scale;
    horizontalSizes.forEach((_, index) => {
        horizontalSizes[index] *= scale;
    });
    verticalSizes.forEach((_, index) => {
        verticalSizes[index] *= scale;
    });
    return newTableLayer;
};

const getTableLayerByXLSX = async (
    frameworkIdentity: string,
    xlsxInfo: XLSXInfo,
    stageInfo: { width: number; height: number; scale: number },
): Promise<{ tableLayer: ITableLayer<true>; isExceed: boolean }> => {
    const { cellsProp, baseCells, styles, isExceed } = getTableInfoByXLSXSheet(xlsxInfo);
    let tableLayer: ITableLayer<true> = {
        ...getTableByCommonPreset({
            cells: cellsProp,
            baseCells,
            styles,
            tableConstants,
        }),
        // 由于导入的表格可能非常大超出画布, 所以这边添加到画布左上角的位置
        top: SIDE_BAR_BOLD,
        left: SIDE_BAR_BOLD,
    };

    // 根据画布做放大处理 (不需要缩小)
    // 表格的整体缩放, 适配画布大小: 扩大倍数 = 画布最长的边的长度 / 600 (没到极限的情况下)
    const expectScale = Math.max(stageInfo.width, stageInfo.height) / 600;
    const scale = Math.max(expectScale, 1);
    tableLayer = getScaledTableLayerByStageSize(tableLayer, scale);
    const allStyle = tableLayer.data.styles[tableLayer.data.allStyleId];
    if (!allStyle.fontSize) {
        allStyle.fontSize = Math.max(MIN_FONT_SIZE, Math.round(0.028 * Math.min(stageInfo.width, stageInfo.height)));
    }
    const framework = getFramework(frameworkIdentity);
    await prepareTableLayerDocumentFonts(framework.service, tableLayer as any as ITableLayer);
    // 必须加载字体后再修复布局, 否则字体不对布局会错乱
    tableLayer = await fixTableLayout({
        originalLayer: tableLayer,
        options: xlsxInfo.sheetOfXlsxPopulate
            ? { mode: 'repair' }
            : { mode: 'contentFit', maxColumnWidth: tableConstants.cellWidth * 3 },
    });
    return {
        tableLayer,
        isExceed,
    };
};

export default getTableLayerByXLSX;
